import type { CustomerLoginCandidateDto } from "@Front/Api/Public/emailAuthCandidatesApi";
import { trimToEmpty } from "@Front/Utils/formatters";
import type { CustomerAuthDisambiguation } from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";

export function distinctOfficeUidsFromCustomerCandidates(rows: CustomerLoginCandidateDto[]): string[] {
	return [...new Set(rows.map((r) => r.officeUid))];
}

export function filterCustomerCandidatesByOfficeUid(
	rows: CustomerLoginCandidateDto[],
	officeUid: string,
): CustomerLoginCandidateDto[] {
	return rows.filter((r) => r.officeUid === officeUid);
}

export type CustomerLoginMulticandidateDecision =
	| { kind: "office_choice" }
	| { kind: "target_choice"; narrowedCandidates?: CustomerLoginCandidateDto[] }
	| { kind: "mail_verify"; disambiguation: CustomerAuthDisambiguation };

function disambiguationFromRow(row: CustomerLoginCandidateDto): CustomerAuthDisambiguation {
	return {
		customerUid: row.customerUid,
		folderUid: row.folderUid,
		officeUid: row.officeUid,
	};
}

/**
 * Rows already restricted to one office (or one office after user picked étude).
 */
export function decideCustomerLoginAfterSingleOfficeScope(rows: CustomerLoginCandidateDto[]): CustomerLoginMulticandidateDecision {
	if (rows.length === 0) {
		return { kind: "target_choice", narrowedCandidates: [] };
	}
	if (rows.length === 1) {
		const row = rows[0];
		return row ? { kind: "mail_verify", disambiguation: disambiguationFromRow(row) } : { kind: "target_choice" };
	}
	const customerUids = new Set(rows.map((r) => r.customerUid));
	if (customerUids.size === 1) {
		const row0 = rows[0];
		return {
			kind: "mail_verify",
			disambiguation: { officeUid: row0?.officeUid, customerUid: row0?.customerUid },
		};
	}
	return { kind: "target_choice", narrowedCandidates: rows };
}

/**
 * Several LIVE client rows for this email on the email step (before optional office pick).
 */
export function decideCustomerLoginMultipleCandidatesFromEmailStep(
	rows: CustomerLoginCandidateDto[],
	invitationScopeFromRouter: boolean,
): CustomerLoginMulticandidateDecision {
	if (rows.length <= 1) {
		return { kind: "target_choice" };
	}
	if (invitationScopeFromRouter) {
		return { kind: "target_choice", narrowedCandidates: rows };
	}
	const offices = distinctOfficeUidsFromCustomerCandidates(rows);
	if (offices.length > 1) {
		return { kind: "office_choice" };
	}
	return decideCustomerLoginAfterSingleOfficeScope(rows);
}

export type OfficeChoiceEntry = { officeUid: string; officeName: string; folderCount: number };

export function buildCustomerLoginOfficeChoiceEntries(rows: CustomerLoginCandidateDto[]): OfficeChoiceEntry[] {
	const byOffice = new Map<string, { officeName: string; count: number }>();
	for (const r of rows) {
		const cur = byOffice.get(r.officeUid);
		const name = trimToEmpty(r.officeName);
		if (!cur) {
			byOffice.set(r.officeUid, { officeName: name, count: 1 });
		} else {
			cur.count += 1;
			if (!cur.officeName && name) {
				cur.officeName = name;
			}
		}
	}
	return [...byOffice.entries()].map(([officeUid, v]) => ({
		officeUid,
		officeName: v.officeName.length > 0 ? v.officeName : officeUid,
		folderCount: v.count,
	}));
}
